import { useEffect, useState } from "react";
import Button from "../Button/Button";
import "./styles.css";
import { UpdateUserInfoUrl } from "../../constants";
import { useTranslation } from "react-i18next";
import { InfoCollection } from "./InfoCollection";
import UserQuestions from "../../config/questions.json";
import flowConfig from "../../config/flowConfig.json";
import { ThickCheckMarkIcon } from "../icons/ThickCheckMarkIcon";
import { CirclePulseAnimation } from "../animations/CirclePulseAnimation/CirclePulseAnimation";
import useSendEmail from "../../hooks/useSendEmail";
import { useConfig } from "../../context/ConfigContext";

export const QuestionsModule = ({
  apiToken,
  fetchAIResults,
  fetchProducts,
  runNextModule,
  onError,
}) => {
  const { t, i18n } = useTranslation(["questionsModule"]);
  const [currentStep, setCurrentStep] = useState(0);
  const { isNLUser } = useConfig();

  const { addUserEmail, sendResultsToKlaviyo } = useSendEmail();

  const [isApiProcessComplete, setIsApiProcessComplete] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [isSaveDone, setIsSaveDone] = useState(false);

  // TODO: Fix exhaustive dependency warning
  useEffect(() => {
    const timerId = setTimeout(async () => {
      if (
        flowConfig.QuestionsModuleFlags.emailEnabled ||
        UserQuestions.length > 0
      ) {
        setCurrentStep(1);
      } else {
        setCurrentStep(2);
        setIsEmailSent(true);
        saveSelectedInfo({ lang: i18n.language });
      }
    }, 1500); // Initial wait for transition

    return () => clearTimeout(timerId);
  }, []);

  useEffect(() => {
    const fetchResults = async () => {
      const resResponse = await fetchAIResults();
      if (resResponse) {
        await fetchProducts();
        return true;
      } else {
        return false;
      }
    };

    if (currentStep === 2 && !isApiProcessComplete && isSaveDone) {
      fetchResults().then((res) => {
        if (res) {
          sendResultsToKlaviyo(apiToken, isNLUser).then(() => {
            setIsEmailSent(true);
            setIsApiProcessComplete(true);
          });
        } else {
          onError();
        }
      });
    }
  }, [i18n.language, apiToken, isSaveDone, currentStep, isApiProcessComplete]);

  const saveSelectedInfo = async (userData) => {
    try {
      const res = await fetch(UpdateUserInfoUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: apiToken,
        },
        body: JSON.stringify(userData),
      });
      if (!res.ok) {
        throw new Error("Unable to connect to api!");
      }
      await new Promise((resolve) => setTimeout(resolve, 2000));
    } catch (error) {
      // TODO: Add error tracking
      onError();
    } finally {
      setIsSaveDone(true);
    }
  };

  return (
    <div className="questions-module">
      {currentStep === 2 &&
      isEmailSent &&
      isSaveDone &&
      isApiProcessComplete ? (
        <div className="analyze-progress select-4-rows">
          <ThickCheckMarkIcon className="result-ready-animaton" />
          <h4 className="analyze-title-success">
            {t("questionsModule:results-success-title")}
          </h4>
          <p className="analyze-info-success">
            {t("questionsModule:results-ready")}
          </p>
        </div>
      ) : (
        <div
          className={`analyze-progress ${currentStep === 0 || currentStep === 2 ? "select-4-rows" : ""}`}
        >
          <CirclePulseAnimation />
          {currentStep !== 1 && (
            <p className="analyze-text-animated">
              {t("questionsModule:analyzing")}...
            </p>
          )}
        </div>
      )}
      {currentStep === 1 && (
        <InfoCollection
          emailEnabled={flowConfig.QuestionsModuleFlags.emailEnabled}
          updateEmail={addUserEmail}
          questionsData={UserQuestions}
          onComplete={(data) => {
            setCurrentStep(2);
            saveSelectedInfo(data);
          }}
        />
      )}
      {currentStep === 2 &&
        isEmailSent &&
        isSaveDone &&
        isApiProcessComplete && (
          <div className="question-module-btn-wrapper-bottom">
            <Button
              text={t("questionsModule:continue")}
              onClick={() => runNextModule()}
            />
          </div>
        )}
    </div>
  );
};
